import React from "react"
import { useTranslation } from "react-i18next"

import { useUnit } from "@/components/unit/use-unit"
import { useAuthentication } from "@/contexts"
import {
  useFormatCurrency,
  useFormatDecimal,
  useFormatPercent,
} from "@/formatters"
import { usePercentSymbol } from "@/hooks/use-percent-symbol"
import { useCurrencySymbol } from "@/hooks/useCurrencySymbol/useCurrencySymbol"
import type { Department } from "@/models/department"
import { UnitName } from "@/models/unit"
import { DataGridColumnHeader } from "@/nzds/data-grid"

import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
  GridRowId,
  GridRowModesModel,
  GridSortModel,
} from "@mui/x-data-grid-pro"

import { baseSiteNumberColumnDef } from "../../../../models"
import type { SiteExplorerGridRecord } from "../../../../services/use-get-site-explorer"
import { useCommonSiteExplorerGridColumns } from "../../../utils/use-common-site-explorer-grid-columns/use-common-site-explorer-grid-columns"
import { useCommonSiteGridColumns } from "../../../utils/use-common-site-grid-columns"

export const defaultColumnVisibilityModel: GridColumnVisibilityModel = {
  electricity_kbtu: false,
  electricity_kbtu_per_sq_ft: false,
  electricity_mt_co2e: false,
  nat_gas_kbtu: false,
  nat_gas_kbtu_per_sq_ft: false,
  nat_gas_mt_co2e: false,
  water_usage_gallons: false,
}

export const defaultPinnedColumns: GridPinnedColumnFields = {
  left: ["rowNumber", "site_name"],
  right: ["actions"],
}

export const defaultSortModel: GridSortModel = [
  {
    field: "site_name",
    sort: "desc",
  },
]

interface UseOverviewGridColumnsProps {
  buildingTypes: string[] | undefined
  departments: Department[] | undefined
  handleRowEditCancelClick: (id: GridRowId) => void
  handleRowEditClick: (id: GridRowId) => void
  handleRowEditSaveClick: (id: GridRowId) => void
  rowModesModel: GridRowModesModel
}

export const useOverviewGridColumns = (
  props: UseOverviewGridColumnsProps
): GridColDef<SiteExplorerGridRecord>[] => {
  const { t } = useTranslation()
  const { canEditSite } = useAuthentication()
  const { formatCurrency } = useFormatCurrency({
    precision: 0,
  })

  const { formatPercent } = useFormatPercent()

  const { formatDecimal: formatDecimalToInteger } = useFormatDecimal({
    precision: 0,
  })

  const currencySymbol = useCurrencySymbol()

  const percentSymbol = usePercentSymbol()

  const gallonUnit = useUnit(UnitName.Gallon)

  const commonSiteGridColumns =
    useCommonSiteGridColumns<SiteExplorerGridRecord>()
  const commonSiteExplorerGridColumns = useCommonSiteExplorerGridColumns(props)

  const columns: GridColDef<SiteExplorerGridRecord>[] = React.useMemo(() => {
    return [
      {
        ...commonSiteGridColumns.rowNumber,
      },
      {
        ...commonSiteGridColumns.siteName,
      },
      {
        ...commonSiteExplorerGridColumns.group,
      },
      {
        ...commonSiteExplorerGridColumns.buildingType,
      },
      {
        ...commonSiteExplorerGridColumns.buildingArea,
      },
      {
        ...commonSiteExplorerGridColumns.combinedEnergyUsage,
      },
      {
        ...commonSiteExplorerGridColumns.combinedEmissions,
      },
      {
        ...commonSiteExplorerGridColumns.combinedEui,
      },
      {
        ...baseSiteNumberColumnDef,
        field: "percent_of_portfolio_usage",
        headerName: t("gridColumn.portfolioContribution.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.portfolioContribution.name")}
            tooltipContent={t("gridColumn.portfolioContribution.description")}
            unit={percentSymbol}
          />
        ),
        valueFormatter: (
          value: SiteExplorerGridRecord["percent_of_portfolio_usage"]
        ) => formatPercent(value),
        width: 224,
      },
      {
        ...commonSiteExplorerGridColumns.electricityUsage,
      },
      {
        ...commonSiteExplorerGridColumns.electricityEmissions,
      },
      {
        ...commonSiteExplorerGridColumns.electricityEui,
      },
      {
        ...commonSiteExplorerGridColumns.naturalGasUsage,
      },
      {
        ...commonSiteExplorerGridColumns.naturalGasEmissions,
      },
      {
        ...commonSiteExplorerGridColumns.naturalGasEui,
      },
      {
        ...baseSiteNumberColumnDef,
        field: "water_usage_gallons",
        headerName: t("gridColumn.waterUsage.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.waterUsage.name")}
            unit={gallonUnit}
          />
        ),
        valueFormatter: (
          value: SiteExplorerGridRecord["water_usage_gallons"]
        ) => formatDecimalToInteger(value),
        width: 124,
      },
      {
        ...baseSiteNumberColumnDef,
        field: "operational_cost",
        headerName: t("gridColumn.operationalCosts.name"),
        renderHeader: () => (
          <DataGridColumnHeader
            headerName={t("gridColumn.operationalCosts.name")}
            unit={currencySymbol}
          />
        ),
        valueFormatter: (value: SiteExplorerGridRecord["operational_cost"]) =>
          formatCurrency(value),
        width: 160,
      },
      ...(canEditSite
        ? [
            {
              ...commonSiteExplorerGridColumns.actions,
            },
          ]
        : []),
    ]
  }, [
    commonSiteGridColumns.rowNumber,
    commonSiteGridColumns.siteName,
    commonSiteExplorerGridColumns.group,
    commonSiteExplorerGridColumns.buildingType,
    commonSiteExplorerGridColumns.buildingArea,
    commonSiteExplorerGridColumns.combinedEnergyUsage,
    commonSiteExplorerGridColumns.combinedEmissions,
    commonSiteExplorerGridColumns.combinedEui,
    commonSiteExplorerGridColumns.electricityUsage,
    commonSiteExplorerGridColumns.electricityEmissions,
    commonSiteExplorerGridColumns.electricityEui,
    commonSiteExplorerGridColumns.naturalGasUsage,
    commonSiteExplorerGridColumns.naturalGasEmissions,
    commonSiteExplorerGridColumns.naturalGasEui,
    commonSiteExplorerGridColumns.actions,
    canEditSite,
    currencySymbol,
    formatDecimalToInteger,
    formatCurrency,
    formatPercent,
    gallonUnit,
    percentSymbol,
    t,
  ])

  return columns
}
